// src/components/notes/AllNotes.js
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import './AllNotes.css';

function AllNotes({ studentId, collapsible = false }) {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { isAuthenticated,userInfo } = useAuth();

  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState('');
  const notesId = `student-notes-${studentId || 'none'}`;
  const showNotes = !collapsible || isExpanded;

  axios.defaults.withCredentials = true;

  const fetchNotes = useCallback(async () => {
    if (!studentId) {
      setNotes([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/notes/getbystudentid/${studentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`          },
        }
      );
      setNotes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching notes', err);
      setNotes([]);
      setError('De gespreksnotities konden niet worden opgehaald.');
    } finally {
      setIsLoading(false);
    }
  }, [studentId, token]);

  useEffect(() => {
    setIsExpanded(false);
    fetchNotes();
  }, [fetchNotes]);

  const handleDelete = async (id, e) => {
    e.preventDefault();

    if (!window.confirm(`Weet je zeker dat je notitie ${id} wilt verwijderen?`)) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/notes/delete/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchNotes();
    } catch (err) {
      console.error('Fout bij verwijderen notitie', err);
    }
  };

  if (!isAuthenticated) {
    return <p>Je bent niet ingelogd.</p>;
  }

  return (
    <div className="container-fluid px-3 px-md-5 mt-4">
      <div className="row mb-4 align-items-center">
        <div className="col-12 col-md-6 text-center text-md-start mb-2 mb-md-0">
          <h2 className="h4">Gespreksnotities</h2>
          {collapsible && <span className="text-muted" aria-live="polite">
            {isLoading ? 'Notities laden…' : error ? 'Aantal niet beschikbaar' : `${notes.length} ${notes.length === 1 ? 'notitie' : 'notities'}`}
          </span>}
        </div>
        <div className="col-12 col-md-6 d-flex flex-wrap gap-2 justify-content-center justify-content-md-end">
          {collapsible && <button type="button" className="btn btn-outline-primary"
            aria-expanded={isExpanded} aria-controls={notesId}
            disabled={isLoading || Boolean(error) || notes.length === 0}
            onClick={() => setIsExpanded(value => !value)}>
            {isExpanded ? 'Verberg notities' : 'Toon alle notities'}
          </button>}
          <button type="button"
            className="btn btn-success"
            onClick={() => navigate(`/notes/new/${studentId}`)}
          >
            <i className="bi bi-plus-circle me-2"></i>Nieuwe notitie
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}
        <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={fetchNotes}>Opnieuw laden</button>
      </div>}
      <div id={notesId} hidden={!showNotes}>
      {showNotes && !isLoading && !error && (
        <div className="row">
          {notes.map((note) => {
            const preview =
              note.content.length > 20
                ? `${note.content.slice(0, 20)}...`
                : note.content;

            return (
              <div key={note.id} className="col-12 col-md-6 col-lg-4 mb-4">
                <div className="card h-100 shadow-sm">
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title">
                      {new Date(note.noteDate).toLocaleDateString()}
                    </h5>
                    <h6 className="card-subtitle mb-2 text-muted">
                      Aangemaakt door: {note.createdBy}
                    </h6>
                    <p className="card-text flex-grow-1">
                      <Link
                        to={`/notes/view/${note.id}`}
                        className="text-decoration-none"
                      >
                        {preview}
                      </Link>
                    </p>
                   {userInfo?.role !== 'docent' && (
                    <div className="mt-2 d-flex gap-2">
                      <Link
                        to={`/notes/update/${note.id}`}
                        className="btn btn-sm btn-warning"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Bewerk
                      </Link>
                      <button type="button"
                        className="btn btn-sm btn-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(note.id, e);
                        }}
                      >
                        Verwijder
                      </button>
                    </div>
                    )}
                    
                  </div>
                </div>
              </div>
            );
          })}

          {notes.length === 0 && (
            <div className="col-12 text-center text-muted">
              Geen notities gevonden.
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

export default AllNotes;